import { createEffect, createStore, sample } from 'effector';
import { useStore } from 'effector-react';
import _ from 'lodash';
import $$auth from 'models/auth';
import $$ep from 'models/ep';
import { $currentQuestionPath } from 'models/light-engine-model';
import { $global } from 'models/store-global';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import PopUpWrapper from 'components/StyledTemplates/PopUpWrapper';
import { Button, Frame } from 'components/StyledTemplates/StyledTemplates';

import EstatePlanService from 'services/EstatePlanService';
import LightEngineService from 'services/LightEngineService';
import ModalsService from 'services/ModalsService';

import useResources from 'hooks/useResources';

import imgCreateEpWithSpouse from 'assets/dashboard-backdrop-create_ep_with_spouse.png';
import imgFinal from 'assets/dashboard-backdrop-final.png';
import imgMarriedOrHaveLifePartner from 'assets/dashboard-backdrop-married_or_have_life_partner.png';
import imgOriginatorOrSpHaveChildren from 'assets/dashboard-backdrop-originator_or_sp_have_children.png';
import imgSignup from 'assets/dashboard-backdrop-signup.png';
import imgChevronLeft from 'assets/icons/modal-assessmen-chevron-left.svg';

const imagesMapping = {
    create_ep_with_spouse: imgCreateEpWithSpouse,
    married_or_have_life_partner: imgMarriedOrHaveLifePartner,
    originator_or_sp_have_children: imgOriginatorOrSpHaveChildren,
    final: imgFinal,
    signup: imgSignup,
};

const $firstAssessmentGroupPaths = createStore<string[]>([]);
const $isLoaded = $$ep.stores.$epQuestionnaire.map(Boolean);
const getFirstAssessmentGroupPaths = createEffect(EstatePlanService.getFirstAssessmentGroupPaths);
const $qInstanceId = $global.map(EstatePlanService.getQInstanceId);

sample({
    source: $isLoaded,
    target: getFirstAssessmentGroupPaths,
});

sample({
    source: getFirstAssessmentGroupPaths.doneData,
    target: $firstAssessmentGroupPaths,
});

sample({
    source: $firstAssessmentGroupPaths,
    clock: getFirstAssessmentGroupPaths.doneData,
    fn: (paths) => paths[0],
    target: $currentQuestionPath,
});

sample({
    clock: $qInstanceId,
    target: createEffect(() => {
        LightEngineService.setCurrentQuestionPath(EstatePlanService.marriedPath);
    }),
});

const ModalAssessment = () => {
    const { t } = useResources(['dashboard_assessment']);

    const navigate = useNavigate();
    const [isFinal, setIsFinal] = useState<boolean>(false);

    const isLoaded = useStore($isLoaded);
    const currentQuestionPath = useStore($currentQuestionPath);

    if (!isLoaded || !currentQuestionPath) {
        return null;
    }

    const groupPaths = EstatePlanService.getFirstAssessmentGroupPaths();
    const isLastQuestion = currentQuestionPath === groupPaths[groupPaths.length - 1];
    const answer = EstatePlanService.getAnswerByPath(currentQuestionPath);
    const isCanGoBack = LightEngineService.isCanGoBack() || isFinal;
    const isCanGoForward = (LightEngineService.isCanGoForward() || (isLastQuestion && _.isBoolean(answer))) && !isFinal;
    const widgetName = currentQuestionPath.split('.').slice(-1)[0];
    const imgKey = (isFinal ? 'final' : widgetName || 'signup') as keyof typeof imagesMapping;
    const label = LightEngineService.eval(t(`${widgetName}`));
    const yesLabel = LightEngineService.eval(t(`${widgetName}_yes`));
    const noLabel = LightEngineService.eval(t(`${widgetName}_no`));

    const goBack = () => {
        if (isFinal) {
            setIsFinal(false);
        } else {
            LightEngineService.goBack();
        }
    };
    const goForward = () => {
        if (!EstatePlanService.isFirstAssessmentGroupComplete() || !isLastQuestion) {
            LightEngineService.goForward();
        } else {
            setIsFinal(true);
        }
    };
    const setAnswer = (value: boolean) => async () => {
        await LightEngineService.setCurrentQuestionAnswer(value);
        goForward();
    };

    const saveAnswers = async () => {
        await EstatePlanService.saveAnswers();
        const qInstanceId = EstatePlanService.getQInstanceId();
        const state = $$auth.$currentParticipant.map((p) => p?.domicile_state_code).getState();
        ModalsService.setPreventClosing('MODAL_ASSESSMENT', false);
        ModalsService.hideModal('MODAL_ASSESSMENT');
        navigate(`/document/EP/${state}/${qInstanceId}`);
    };

    return (
        <PopUpWrapper
            name="MODAL_ASSESSMENT"
            size="lg"
            withCross={false}
            preventClosing={true}
            extra=".modal-dialog { position: absolute; top: 0; transform: translate(90px, 120px) !important }; @media (max-width: 991px) { .modal-dialog { transform: translate(0px, 120px) !important }; }"
        >
            <InnerComponentWrapper className="dashboard-assessment" imgSrc={imagesMapping[imgKey]}>
                <div className="d-flex gap-2">
                    <NavButton direction="left" disabled={!isCanGoBack} onClick={goBack} />
                    <NavButton direction="right" disabled={!isCanGoForward} onClick={goForward} />
                </div>
                {isFinal ? (
                    <>
                        <h3 className="serif">{t('final')}</h3>
                        <Button data-qaid="finish_button" onClick={saveAnswers}>
                            {t('finish')}
                        </Button>
                    </>
                ) : (
                    <>
                        <small>{t('tell_us')}</small>
                        <h3 className="serif">{label}</h3>
                        <div className="d-flex gap-2">
                            <Button data-qaid="yes_button" onClick={setAnswer(true)}>
                                {yesLabel}
                            </Button>
                            <Button data-qaid="no_button" onClick={setAnswer(false)}>
                                {noLabel}
                            </Button>
                        </div>
                    </>
                )}
            </InnerComponentWrapper>
        </PopUpWrapper>
    );
};

const NavButton = styled.button<{ direction: 'left' | 'right' }>`
    border: none;
    outline: none;

    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: ${({ direction }) => (direction === 'left' ? '0.25rem 0 0 0.25rem' : '0 0.25rem 0.25rem 0')};
    width: 34px;
    height: 34px;
    background: #e0e0e0;

    &:after {
        content: '';
        display: flex;
        width: 10px;
        height: 16px;
        background: url('${imgChevronLeft}') no-repeat center center / contain;
        transform: ${({ direction }) => (direction === 'left' ? 'rotate(0deg)' : 'rotate(180deg)')};
    }

    &:disabled {
        opacity: 0.3;
    }
`;

const InnerComponentWrapper = styled(Frame)<{ imgSrc?: string }>`
    z-index: 1;
    background-color: #ffffff;
    position: relative;
    border: 1px solid #dfdfdf;
    border-radius: 8px;
    padding: 14px 253px 39px 54px;
    min-height: 327px;
    justify-content: center;
    align-items: flex-start;
    gap: 1rem;

    &:before {
        content: '';
        position: absolute;
        z-index: 2;
        right: -1px;
        top: -1px;
        width: 365px;
        height: 323px;
        ${({ imgSrc }) =>
            imgSrc &&
            css`
                background: url('${imgSrc}') no-repeat center center / cover;
            `}
        -webkit-clip-path: polygon(0 0, 420px 370px, 420px 0);
        clip-path: polygon(0 0, 420px 370px, 420px 0);
        border-top-right-radius: 8px;
    }

    @media (max-width: 768px) {
        padding: 14px 40px 39px 40px;

        &:before {
            background: none;
        }
    }

    @media (max-width: 476px) {
        padding: 14px 14px 24px 14px;

        &:before {
            background: none;
        }
    }

    small {
        line-height: 120%;
        color: #6c757d;
    }
`;

export default ModalAssessment;
